import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { 
  Filter, 
  X, 
  CalendarIcon, 
  MapPin, 
  Briefcase, 
  Building2,
  FileText,
  RotateCcw
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Régions de Guinée
const REGIONS = [
  "Conakry",
  "Boké",
  "Kindia",
  "Mamou",
  "Labé",
  "Faranah",
  "Kankan",
  "Nzérékoré",
];

// Secteurs d'activité officiels IGT
const SECTEURS = [
  "BTP, Génie Civil, Immobilier",
  "Agriculture, Agro-industrie, Elevage, Pêche",
  "Ambassade",
  "Automobile et Mécanique Générale",
  "Banques, IMF, Monnaies Electroniques, Assurances",
  "Diamantaires, Orpailleurs et Assimilés",
  "Enseignement Privé",
  "Environnement, Assainissement, Bois",
  "EPA",
  "Hydrocarbure et énergie",
  "Industrie Manufacturière, Commerce",
  "Institution",
  "Mines et Carrières",
  "Prestation de Services, Bureau d'Etude Technique",
  "SA Etatique",
  "Santé",
  "Sécurité Privée",
  "TIC, Télécom, Economie numérique, Presse",
  "Tourisme, Artisanat, Boulangerie, Patisserie",
  "Transport, Transit, Consignation",
];

// Statuts de bilan
const STATUTS_BILAN = [
  { value: "brouillon", label: "Brouillon" },
  { value: "soumis", label: "Soumis" },
  { value: "en_controle", label: "En contrôle" },
  { value: "valide", label: "Validé" },
  { value: "rejete", label: "Rejeté" },
];

export default function AdvancedFilters({ 
  onFiltersChange, 
  showRegion = true,
  showSecteur = true,
  showStatut = true,
  showCabinet = true,
  showPeriode = true,
  showSearch = false,
  searchPlaceholder = "Rechercher...",
  compact = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [cabinets, setCabinets] = useState([]);
  const [loadingCabinets, setLoadingCabinets] = useState(false);
  
  // Filtres
  const [filters, setFilters] = useState({
    search: "",
    annee: "all",
    trimestre: "all",
    dateDebut: null,
    dateFin: null,
    region: "all",
    secteur: "all",
    statut: "all",
    cabinet_id: "all",
  });

  // Années disponibles (5 dernières années)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

  // Charger les cabinets
  useEffect(() => {
    if (showCabinet) {
      fetchCabinets();
    }
  }, [showCabinet]);

  const fetchCabinets = async () => {
    setLoadingCabinets(true);
    try {
      const response = await axios.get(`${API_URL}/cabinets`);
      setCabinets(response.data.filter(c => c.status === "actif"));
    } catch (error) {
      console.error(/* PROD */ "Erreur chargement cabinets:", error);
    } finally {
      setLoadingCabinets(false);
    }
  };

  // Notifier les changements de filtres
  useEffect(() => {
    onFiltersChange?.(filters);
  }, [filters]);

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({
      search: "",
      annee: "all",
      trimestre: "all",
      dateDebut: null,
      dateFin: null,
      region: "all",
      secteur: "all",
      statut: "all",
      cabinet_id: "all",
    });
  };

  // Compter les filtres actifs
  const activeFiltersCount = Object.entries(filters).filter(([key, value]) => {
    if (key === "search") return value.length > 0;
    if (key === "dateDebut" || key === "dateFin") return value !== null;
    return value !== "all";
  }).length;

  // Version compacte (barre horizontale)
  if (compact) {
    return (
      <div className="flex flex-wrap items-center gap-3 p-4 bg-slate-50 rounded-lg border">
        {showSearch && (
          <div className="flex-1 min-w-[200px]">
            <Input
              placeholder={searchPlaceholder}
              value={filters.search}
              onChange={(e) => updateFilter("search", e.target.value)}
              className="h-9"
            />
          </div>
        )}

        {showPeriode && (
          <Select value={filters.annee} onValueChange={(v) => updateFilter("annee", v)}>
            <SelectTrigger className="w-[120px] h-9">
              <SelectValue placeholder="Année" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              {years.map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {showRegion && (
          <Select value={filters.region} onValueChange={(v) => updateFilter("region", v)}>
            <SelectTrigger className="w-[140px] h-9">
              <SelectValue placeholder="Région" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes régions</SelectItem>
              {REGIONS.map(region => (
                <SelectItem key={region} value={region}>{region}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {showStatut && (
          <Select value={filters.statut} onValueChange={(v) => updateFilter("statut", v)}>
            <SelectTrigger className="w-[130px] h-9">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              {STATUTS_BILAN.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {activeFiltersCount > 0 && (
          <Button variant="ghost" size="sm" onClick={resetFilters} className="h-9">
            <RotateCcw className="w-4 h-4 mr-1" />
            Réinitialiser
          </Button>
        )}
      </div>
    );
  }

  // Version complète (panneau déroulant)
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        {showSearch && (
          <div className="flex-1">
            <Input
              placeholder={searchPlaceholder}
              value={filters.search}
              onChange={(e) => updateFilter("search", e.target.value)}
              className="h-10"
            />
          </div>
        )}

        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2">
              <Filter className="w-4 h-4" />
              Filtres avancés
              {activeFiltersCount > 0 && (
                <Badge className="ml-1 bg-[#1B4D3E]">{activeFiltersCount}</Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[400px] p-4" align="end">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Filtres avancés
                </h4>
                {activeFiltersCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={resetFilters}>
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Réinitialiser
                  </Button>
                )}
              </div>

              {/* Période */}
              {showPeriode && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm font-medium">
                    <CalendarIcon className="w-4 h-4 text-slate-500" />
                    Période
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={filters.annee} onValueChange={(v) => updateFilter("annee", v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Année" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Toutes les années</SelectItem>
                        {years.map(year => (
                          <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select value={filters.trimestre} onValueChange={(v) => updateFilter("trimestre", v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Trimestre" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        <SelectItem value="T1">T1 (Jan-Mar)</SelectItem>
                        <SelectItem value="T2">T2 (Avr-Juin)</SelectItem>
                        <SelectItem value="T3">T3 (Juil-Sep)</SelectItem>
                        <SelectItem value="T4">T4 (Oct-Déc)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Région */}
              {showRegion && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm font-medium">
                    <MapPin className="w-4 h-4 text-slate-500" />
                    Région
                  </Label>
                  <Select value={filters.region} onValueChange={(v) => updateFilter("region", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une région" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les régions</SelectItem>
                      {REGIONS.map(region => (
                        <SelectItem key={region} value={region}>{region}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Secteur */}
              {showSecteur && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm font-medium">
                    <Briefcase className="w-4 h-4 text-slate-500" />
                    Secteur d'activité
                  </Label>
                  <Select value={filters.secteur} onValueChange={(v) => updateFilter("secteur", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un secteur" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les secteurs</SelectItem>
                      {SECTEURS.map(secteur => (
                        <SelectItem key={secteur} value={secteur}>{secteur}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Statut */}
              {showStatut && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm font-medium">
                    <FileText className="w-4 h-4 text-slate-500" />
                    Statut du bilan
                  </Label>
                  <Select value={filters.statut} onValueChange={(v) => updateFilter("statut", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un statut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les statuts</SelectItem>
                      {STATUTS_BILAN.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Cabinet */}
              {showCabinet && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm font-medium">
                    <Building2 className="w-4 h-4 text-slate-500" />
                    Cabinet
                  </Label>
                  <Select 
                    value={filters.cabinet_id} 
                    onValueChange={(v) => updateFilter("cabinet_id", v)}
                    disabled={loadingCabinets}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={loadingCabinets ? "Chargement..." : "Sélectionner un cabinet"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les cabinets</SelectItem>
                      {cabinets.map(cabinet => (
                        <SelectItem key={cabinet.id} value={cabinet.id}>
                          {cabinet.nom}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Boutons d'action */}
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" size="sm" onClick={() => setIsOpen(false)}>
                  Fermer
                </Button>
                <Button size="sm" className="bg-[#1B4D3E] hover:bg-[#2d7a62]" onClick={() => setIsOpen(false)}>
                  Appliquer
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {activeFiltersCount > 0 && (
          <Button variant="ghost" size="sm" onClick={resetFilters}>
            <X className="w-4 h-4 mr-1" />
            Effacer ({activeFiltersCount})
          </Button>
        )}
      </div>

      {/* Affichage des filtres actifs */}
      {activeFiltersCount > 0 && (
        <div className="flex flex-wrap gap-2">
          {filters.annee !== "all" && (
            <Badge variant="secondary" className="gap-1">
              <CalendarIcon className="w-3 h-3" />
              {filters.annee}
              <X className="w-3 h-3 cursor-pointer" onClick={() => updateFilter("annee", "all")} />
            </Badge>
          )}
          {filters.trimestre !== "all" && (
            <Badge variant="secondary" className="gap-1">
              {filters.trimestre}
              <X className="w-3 h-3 cursor-pointer" onClick={() => updateFilter("trimestre", "all")} />
            </Badge>
          )}
          {filters.region !== "all" && (
            <Badge variant="secondary" className="gap-1">
              <MapPin className="w-3 h-3" />
              {filters.region}
              <X className="w-3 h-3 cursor-pointer" onClick={() => updateFilter("region", "all")} />
            </Badge>
          )}
          {filters.secteur !== "all" && (
            <Badge variant="secondary" className="gap-1">
              <Briefcase className="w-3 h-3" />
              {filters.secteur.substring(0, 20)}...
              <X className="w-3 h-3 cursor-pointer" onClick={() => updateFilter("secteur", "all")} />
            </Badge>
          )}
          {filters.statut !== "all" && (
            <Badge variant="secondary" className="gap-1">
              <FileText className="w-3 h-3" />
              {STATUTS_BILAN.find(s => s.value === filters.statut)?.label}
              <X className="w-3 h-3 cursor-pointer" onClick={() => updateFilter("statut", "all")} />
            </Badge>
          )}
          {filters.cabinet_id !== "all" && (
            <Badge variant="secondary" className="gap-1">
              <Building2 className="w-3 h-3" />
              {cabinets.find(c => c.id === filters.cabinet_id)?.nom?.substring(0, 15)}...
              <X className="w-3 h-3 cursor-pointer" onClick={() => updateFilter("cabinet_id", "all")} />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}

// Export des constantes pour réutilisation
export { REGIONS, SECTEURS, STATUTS_BILAN };
